import { isChatStatusBusy, useStores } from '@littlebird/sdk';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { SidebarSectionHeading } from '@/components/blocks/Layout/Sidebar/SidebarSectionContainer';
import { SECTION_GAP } from '@/components/blocks/Layout/Sidebar/style';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { YStack } from '@/components/ui/layout/layout';
import {
  ROUND_ICON_BUTTON_EDGE_INSET,
  RoundIconButton,
} from '@/design-system-v2/round-icon-button';
import { SidePanel } from '@/design-system-v2/side-panel';
import { SidebarRow } from '@/design-system-v2/sidebar-row';
import { isNewTabClick, useTabs } from '@/features/tabs';
import { cn } from '@/lib/utils';
import { Routes } from '@/types/routes';

import { AgentAvatar } from './agents-avatar';
import { type BirdLook } from './agents-bird.look';
import { readAgentActivity } from './agents.hooks';
import {
  agentsStore,
  type AgentEntry,
  LAUNCHED_AGENT_NAME,
} from './agents.store';
import {
  isAgentPrimingMessage,
  isAgentProfileUpdateMessage,
} from './agents.utils';

const HATCH_GRACE_MS = 60_000;

export const AgentsSidebar = observer(() => {
  const { threads, messages } = useStores();
  const { chatId } = useParams({ strict: false });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { openTab } = useTabs();

  const agentChatIdsKey = agentsStore.agents
    .flatMap((agent) => (agent.chatId ? [agent.chatId] : []))
    .join(',');
  const agentChatIds = useMemo(
    () => agentChatIdsKey.split(',').filter(Boolean),
    [agentChatIdsKey]
  );
  useEffect(() => {
    if (agentChatIds.length === 0) return;
    void messages.loadThreadsMessages(agentChatIds);
  }, [messages, agentChatIds]);

  const openPath = (path: string, event?: React.MouseEvent) => {
    if (event && isNewTabClick(event)) openTab({ path });
    else void navigate({ to: path });
  };

  const leaveIfOpen = (agent: AgentEntry) => {
    if (chatId === agent.chatId)
      void navigate({ to: Routes.AgentsNewTask as string });
  };

  const lastUserMessageMs = (agent: AgentEntry) => {
    const lastUserMessage = agent.chatId
      ? messages
          .getMessagesByThreadId(agent.chatId)
          .filter(
            (message) =>
              message.role === 'user' &&
              !isAgentPrimingMessage(message) &&
              !isAgentProfileUpdateMessage(message)
          )
          .at(-1)
      : undefined;
    return lastUserMessage
      ? lastUserMessage.createdAt.getTime()
      : new Date(agent.createdAt).getTime();
  };

  const [firstSeenAgentIds] = useState(
    () => new Set(agentsStore.agents.map((agent) => agent.agentId))
  );
  const [mountedAt] = useState(() => Date.now());

  const stillNaming = (agent: AgentEntry) =>
    agent.name === LAUNCHED_AGENT_NAME &&
    agent.chatId !== undefined &&
    isChatStatusBusy(threads.get(agent.chatId)?.status);

  const recent = agentsStore.recent
    .filter((agent) => !stillNaming(agent))
    .map((agent) => ({ agent, sortMs: lastUserMessageMs(agent) }))
    .sort((a, b) => b.sortMs - a.sortMs)
    .map(({ agent }) => agent);

  const update = (action: Promise<unknown>, after?: () => void) =>
    action.then(after).catch(() => toast.error('Failed to update the agent'));

  const agentRow = (agent: AgentEntry) => (
    <AgentListRow
      key={agent.agentId}
      agentId={agent.agentId}
      chatId={agent.chatId}
      name={agent.name}
      introPending={!agent.description}
      bird={agent.bird}
      hatch={
        !firstSeenAgentIds.has(agent.agentId) &&
        Date.parse(agent.createdAt) > mountedAt - HATCH_GRACE_MS
      }
      active={chatId === agent.chatId}
      pinned={agent.pinnedAt !== null}
      onClick={(event) =>
        agent.chatId && openPath(`/chats/${agent.chatId}`, event)
      }
      onTogglePin={() =>
        update(agentsStore.setPinned(agent.agentId, agent.pinnedAt === null))
      }
      onToggleArchive={() =>
        update(agentsStore.setArchived(agent.agentId, true), () =>
          leaveIfOpen(agent)
        )
      }
      onDelete={() =>
        update(agentsStore.removeAgent(agent.agentId), () => leaveIfOpen(agent))
      }
    />
  );

  return (
    <>
      <SidePanel.PrimaryAction>
        <SidebarRow.Button
          leading={<SidebarRow.AddIcon />}
          selected={pathname.startsWith(Routes.AgentsNewTask)}
          onClick={(event) => openPath(Routes.AgentsNewTask, event)}
        >
          New task
        </SidebarRow.Button>
      </SidePanel.PrimaryAction>
      <YStack className="min-h-0 flex-1 overflow-y-auto scrollbar-hide">
        <AgentsSidebarGroup title="Pinned">
          {agentsStore.pinned.map(agentRow)}
        </AgentsSidebarGroup>
        <AgentsSidebarGroup title="Recents">
          {recent.map(agentRow)}
        </AgentsSidebarGroup>
      </YStack>
    </>
  );
});

const AgentsSidebarGroup = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode[];
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (children.length === 0) return null;

  return (
    <YStack className={cn('shrink-0', SECTION_GAP)}>
      <SidebarSectionHeading
        isExpanded={isExpanded}
        onExpandedChange={setIsExpanded}
      >
        {title}
      </SidebarSectionHeading>
      {isExpanded && <YStack className="mt-0.5 gap-0.5">{children}</YStack>}
    </YStack>
  );
};

type AgentListRowProps = {
  agentId: string | undefined;
  chatId: string | undefined;
  name: string;
  bird: BirdLook;
  hatch?: boolean;
  introPending?: boolean;
  active: boolean;
  pinned?: boolean;
  onClick: (event?: React.MouseEvent) => void;
  onTogglePin?: () => void;
  onToggleArchive?: () => void;
  onDelete?: () => void;
};

export const AgentListRow = observer(
  ({
    agentId,
    chatId,
    name,
    bird,
    hatch = false,
    introPending = false,
    active,
    pinned = false,
    onClick,
    onTogglePin,
    onToggleArchive,
    onDelete,
  }: AgentListRowProps) => {
    const stores = useStores();
    const { threads } = stores;
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [hovered, setHovered] = useState(false);

    useEffect(() => {
      if (chatId) {
        void threads.ensureLoaded(chatId);
      } else if (agentId) {
        void threads.loadThreadForAgent(agentId);
      }
    }, [threads, chatId, agentId]);

    const { activity, isUnread, lastMessageIso } = readAgentActivity(stores, {
      agentId,
      chatId,
      introPending,
      active,
    });

    useEffect(() => {
      if (active && agentId && lastMessageIso) {
        agentsStore.markViewed(agentId, lastMessageIso);
      }
    }, [active, agentId, lastMessageIso]);

    // Archive appears on mouse hover; on touch it is permanently visible
    const showArchive = !!onToggleArchive && !pinned;

    const row = (
      <SidebarRow.Root
        selected={active}
        onPointerEnter={(event) => {
          // hover state is a mouse interaction; a touch tap must not change
          // visible content here or WebKit withholds the click
          if (event.pointerType !== 'mouse') return;
          setHovered(true);
        }}
        onPointerLeave={(event) => {
          if (event.pointerType !== 'mouse') return;
          setHovered(false);
        }}
        onFocus={() => setHovered(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setHovered(false);
          }
        }}
      >
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 self-stretch rounded-xl text-left focus-ring"
          onClick={onClick}
        >
          <SidebarRow.Leading unread={isUnread}>
            <AgentAvatar
              bird={bird}
              activity={activity}
              hatch={hatch}
              statusName={name}
              className="size-[15px]"
            />
          </SidebarRow.Leading>
          <SidebarRow.Label>{name}</SidebarRow.Label>
        </button>
        {showArchive && (
          <RoundIconButton
            icon={Icons.archive}
            label={`Archive ${name}`}
            size="xs"
            className={cn(
              '-my-0.5 hidden touch:inline-flex',
              ROUND_ICON_BUTTON_EDGE_INSET.xs,
              hovered && 'inline-flex'
            )}
            onClick={(event) => {
              event.stopPropagation();
              onToggleArchive();
            }}
          />
        )}
      </SidebarRow.Root>
    );

    if (!onDelete) return row;

    return (
      <ContextMenu
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(false);
        }}
      >
        <ContextMenuTrigger asChild>{row}</ContextMenuTrigger>
        <ContextMenuContent className="min-w-40">
          {onTogglePin && (
            <ContextMenuItem
              icon={pinned ? Icons.unpin : Icons.pin}
              onClick={onTogglePin}
            >
              {pinned ? 'Unpin' : 'Pin'}
            </ContextMenuItem>
          )}
          {onToggleArchive && (
            <ContextMenuItem icon={Icons.archive} onClick={onToggleArchive}>
              Archive
            </ContextMenuItem>
          )}
          {agentId && (
            <ContextMenuItem
              icon={Icons.edit}
              onClick={() => {
                onClick();
                agentsStore.setProfileOpen(true);
              }}
            >
              Edit agent
            </ContextMenuItem>
          )}
          <ContextMenuItem
            icon={confirmDelete ? Icons.check : Icons.trash}
            className="text-danger-9"
            onClick={(event) => {
              if (!confirmDelete) {
                event.preventDefault();
                event.stopPropagation();
                setConfirmDelete(true);
                return;
              }
              setConfirmDelete(false);
              onDelete();
            }}
          >
            {confirmDelete ? 'Confirm delete' : 'Delete agent'}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    );
  }
);
